import { useMemo } from "react";
import type { ReactLocalization } from "@fluent/react";
import User from "byzantine/src/User";

import type { CitizenshipChoice, PrimaryChoiceConfig } from "./types";

type UsePrimaryChoiceArgs = {
  l10n: ReactLocalization;
  isActiveStepCitizenshipSelect: boolean;
  userCitizenship?: CitizenshipChoice;
};

type DerivePrimaryChoiceConfigArgs = {
  isActiveStepCitizenshipSelect: boolean;
  userCitizenship?: CitizenshipChoice;
  citizenshipChoice: PrimaryChoiceConfig;
  userCategoryChoice: PrimaryChoiceConfig;
};

const derivePrimaryChoiceConfig = ({
  isActiveStepCitizenshipSelect,
  userCitizenship,
  citizenshipChoice,
  userCategoryChoice,
}: DerivePrimaryChoiceConfigArgs): PrimaryChoiceConfig | null => {
  if (isActiveStepCitizenshipSelect) {
    return citizenshipChoice;
  }

  if (userCitizenship === "us_citizen") {
    return userCategoryChoice;
  }

  if (userCitizenship === "foreign_national") {
    return {
      ...citizenshipChoice,
      isHidden: true,
    };
  }

  return null;
};

const usePrimaryChoice = ({
  l10n,
  isActiveStepCitizenshipSelect,
  userCitizenship,
}: UsePrimaryChoiceArgs): PrimaryChoiceConfig | null =>
  useMemo(() => {
    const citizenshipChoice: PrimaryChoiceConfig = {
      field: "user_citizenship",
      name: "user_citizenship",
      label: l10n.getString(
        "label-select-citizenship",
        null,
        "Are you a U.S. citizen or permanent resident?"
      ),
      options: {
        [l10n.getString("label-citizenship-select-yes", null)]: "us_citizen",
        [l10n.getString("label-citizenship-select-no", null)]:
          "foreign_national",
      },
      isHidden: false,
    };
    const userCategoryChoice: PrimaryChoiceConfig = {
      field: "user_category",
      name: "user_category",
      label: l10n.getString(
        "label-your-account-type",
        null,
        "What type of account do you have?"
      ),
      options: {
        [l10n.getString("label-personal", null, "Personal")]: User.PERSONAL,
        [l10n.getString("label-business", null, "Business")]: User.BUSINESS,
      },
      isHidden: false,
    };

    return derivePrimaryChoiceConfig({
      isActiveStepCitizenshipSelect,
      userCitizenship,
      citizenshipChoice,
      userCategoryChoice,
    });
  }, [isActiveStepCitizenshipSelect, l10n, userCitizenship]);

export default usePrimaryChoice;
